import React, { useMemo, useState } from "react";
import { createAPITierSubscriptions } from "@utils/stripe";
import CustomBoloSlider from "@components/Slider/CustomReactSlider";
import { createUTMQueryParams } from "@utils/signature_owner";
import { FaCheck } from "react-icons/fa";
import { BsCheckCircleFill } from "react-icons/bs";
import { MdStars } from "react-icons/md";
import { HiCheck } from "react-icons/hi";
import { ThreeDots } from "react-loader-spinner";
import GradientCheckIcon from "@components/Cards/GradientCheckIcon";
import CustomBoloSlider2 from "@components/Slider/CustomBoloSlider2";
import {
  CLMButtonPrimary1,
  CLMButtonSecondary,
  CLMSecondaryBorder,
} from "components/CLM/v3/Buttons";

const ApiAddonPricingCard2 = ({
  currentPage,
  title,
  subTitle,
  bgColor = "bg-white",
  cardBorder,
}) => {
  const [priceLoading, setPriceLoading] = useState(false);
  const featureDataObject = useMemo(() => {
    return {
      title: "BoloSign API",
      desc: "Automate your signature workflow by integrating our API into your systems.",
      includes: {
        api: ["BoloSign Webhooks", "BoloSign Embeddings"],
        embedding: ["BoloSign Webhooks", "BoloSign API"],
      },
      features: [
        "BoloSign API Access",
        "BoloSign Webhooks",
        "BoloSign Embeddings",
        "Customizable Branding",
        "Session Control",
        "Create PDFs",
        "Send PDFs for signature",
        "Fetch PDF status in your website",
        "Zapier Integration",
        "Pabbly Integration",
        "Make (Integromat) Integration",
        "And more...",
      ],
    };
  }, []);

  const envelopeCountAndPrice = [
    { count: 100, price: 40 },
    { count: 200, price: 72 },
    { count: 500, price: 180 },
    { count: 1000, price: 360 },
    { count: 2000, price: 720 },
    { count: 3000, price: "Contact Sales" },
  ];

  const [apiPrice, setApiPrice] = useState(envelopeCountAndPrice[1].price);
  const [documentSliderCount, setDocumentSliderCount] = useState(
    envelopeCountAndPrice[1].count
  );

  //   const ownerPlanDetails = getOwnerPlanDetails();
  //   const currentPlanTier = ownerPlanDetails?.currentPlan?.tier;
  //   const isCurrentPlanActive = ownerPlanDetails.currentPlan?.isActive;

  const handleSliderChange = (newValue) => {
    setDocumentSliderCount(newValue);
    const selectedPrice = envelopeCountAndPrice.find(
      (item) => item.count === newValue
    );
    if (selectedPrice) {
      setApiPrice(selectedPrice.price);
    }
  };

  return (
    <div
      className={` flex flex-col gap-y-2 w-full lg:p-6 p-4 md:p-10 border-2 border-slate-200 rounded-3xl relative mb-20 `}
    >
      <div>
        <h2 className="text-4xl font-bold text-left">
          {title || featureDataObject.title}
        </h2>
        <p className="text-base text-left text-gray-600 mt-4 ">
          {subTitle || featureDataObject.desc}
        </p>
        <div className="flex md:gap-8 gap-2 mt-4 lg:flex-row flex-col">
          {featureDataObject.includes[currentPage]?.map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <GradientCheckIcon />
              <span className={`text-base leading-[24px] font-semibold  mb-1`}>
                {item}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-4">
        <div className="space-y-2 min-h-[100px]">
          {apiPrice === "Contact Sales" ? (
            <div className="flex justify-start items-center w-full min-h-[100px]">
              <p className="text-5xl font-bold text-black">Contact Sales</p>
            </div>
          ) : (
            <>
              <div className="flex justify-start items-end w-full h-full">
                <p className="text-6xl font-bold">
                  <span className="text-4xl mr-1">$</span>
                  {apiPrice}
                </p>
                <p className="text-2xl ml-1">/mo</p>
              </div>
              <p className="text-left text-base text-gray-600">
                <span className="font-semibold text-gray-700">$0.5</span> for
                each document beyond
                <span className="font-semibold text-gray-700 mx-1">
                  {documentSliderCount}
                </span>
                limit.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="mx-2">
        <CustomBoloSlider2
          defaultValue={documentSliderCount}
          min={100}
          max={3000}
          marks={envelopeCountAndPrice.map((item) => ({
            value: item.count,
            label: `${item.count}`,
          }))}
          onChange={handleSliderChange}
          currentActiveMark={0}
        />
      </div>

      <div className="w-full flex justify-start items-center gap-3 md:flex-row flex-col my-8">
        <CLMButtonPrimary1
          buttonText={
            apiPrice === "Contact Sales" ? "Contact Sales" : "Subscribe Now"
          }
          className="w-full md:w-auto"
          onClick={async () => {
            if (apiPrice === "Contact Sales") {
              window?.Intercom("showNewMessage");
            } else {
              setPriceLoading(true);
              await createAPITierSubscriptions({
                documentsCount: documentSliderCount,
              });
              setPriceLoading(false);
            }
          }}
        />
        <CLMSecondaryBorder
          className="w-full md:w-auto"
          onClick={() => {
            window.open(
              `https://signature.boloforms.com/register?from=API_PAGE${createUTMQueryParams(
                {
                  from: "API_PAGE",
                }
              )}`,
              "_self"
            );
          }}
          buttonText={"Try Sandbox Testing for $0"}
        />
      </div>

      {/* Simplified features section using auto-fit grid */}
      <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {featureDataObject.features.map((feature, index) => (
          <div key={index} className="flex items-center gap-2">
            <GradientCheckIcon />
            <span className={`text-base leading-[24px] font-medium  `}>
              {feature}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ApiAddonPricingCard2;
